/**
 * MaestroFeedbackControls — Thumbs up/down on assistant messages
 * MAESTRO:P0D
 *
 * Renders below each assistant bubble.  Submits feedback via maestroApi.submitFeedback().
 * After submission the active button is highlighted and both are disabled to prevent double-submit.
 */

import { useState, useCallback } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { maestroApi } from '../../../services/maestroApi';

/**
 * @param {Object} props
 * @param {string} props.messageId - UUID of the assistant message
 */
export default function MaestroFeedbackControls({ messageId }) {
  const [submitted, setSubmitted] = useState(null); // 'up' | 'down' | null
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = useCallback(async (rating) => {
    /* v8 ignore next -- defensive guard; DOM disabled={isSubmitting||submitted} prevents reach */
    if (isSubmitting || submitted) return;
    setIsSubmitting(true);
    try {
      await maestroApi.submitFeedback(messageId, { rating });
      setSubmitted(rating);
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[MaestroFeedback] submit error:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, submitted, messageId]);

  const baseClass = `
    p-1.5 rounded transition-all duration-150 min-h-[2rem] min-w-[2rem] flex items-center justify-center
    focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 focus:ring-offset-zinc-950
    disabled:cursor-not-allowed
  `;

  return (
    <div
      className="flex items-center gap-1 mt-2"
      role="group"
      aria-label="Rate this response"
    >
      <button
        type="button"
        aria-label="Helpful"
        aria-pressed={submitted === 'up'}
        disabled={!!submitted || isSubmitting}
        onClick={() => submit('up')}
        className={`${baseClass} ${
          submitted === 'up'
            ? 'text-green-400 bg-green-500/10'
            : 'text-zinc-600 hover:text-green-400 hover:bg-green-500/10'
        } ${submitted && submitted !== 'up' ? 'opacity-30' : ''}`}
      >
        <ThumbsUp size={13} aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label="Not helpful"
        aria-pressed={submitted === 'down'}
        disabled={!!submitted || isSubmitting}
        onClick={() => submit('down')}
        className={`${baseClass} ${
          submitted === 'down'
            ? 'text-red-400 bg-red-500/10'
            : 'text-zinc-600 hover:text-red-400 hover:bg-red-500/10'
        } ${submitted && submitted !== 'down' ? 'opacity-30' : ''}`}
      >
        <ThumbsDown size={13} aria-hidden="true" />
      </button>
      {submitted && (
        <span className="text-[10px] text-zinc-600 font-mono ml-1">
          {submitted === 'up' ? 'Thanks!' : 'Noted.'}
        </span>
      )}
    </div>
  );
}
